import { ChangeEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ControllerRenderProps,
  FieldError,
  useController,
  useForm,
} from 'react-hook-form';
import {
  ISellerSignUpData,
  ISellerSignUpKeys,
  METAL_TYPE,
  useValidateSellerOrderPhotos,
} from '@frontend/api';
import { Translate } from '@frontend/translation';
import {
  Button,
  CheckBox,
  FilePicker,
  humanFileSize,
  IFilePickerFile,
  Select,
  TextField,
  TextArea,
} from '@frontend/ui-elements';
import { yupResolver } from '@hookform/resolvers/yup';
import { SelectChangeEvent, Typography, useTheme } from '@mui/material';
import {
  ButtonsBox,
  DualBox,
  FieldsWrapper,
  FormBox,
  FormContainer,
} from '../SignUpForms/SignUpForms.css';
import { SellerSignUpFormProps } from './SellerSignUpForm.types';
import FormType from '@customer-platform-shared/components/Forms/utils/FormType.types';
import PhoneField from '@customer-platform-shared/components/ui-csr/PhoneField/PhoneField';
import { useFormTracking } from '@customer-platform-shared/components/Forms/utils/useFormTracking';
import { buildSelectDataByOptionsAndNamespace } from '@customer-platform-shared/components/Forms/utils/utils';
import { head } from 'lodash';
import { RiArrowDownSFill } from '@remixicon/react';
import { useImageValidationStatus } from '@customer-platform-shared/components/Forms/utils/useImageValidationStatus';
import {
  MAX_FILE_SIZE,
  MIN_REQUIRED_FILES,
  SUPPORTED_FORMATS,
} from '@customer-platform-shared/components/Forms/ListingForm/ListingForm.const';
import {
  MAX_NUMBER_AFTER_DECIMAL,
  MIN_QUANTITY_REQUIRED_MT,
} from '@customer-platform-shared/components/Forms/Forms.const';
import getSellerSignUpFormSchema from './SellerSignUpForm.utils';

export default function SellerSignUpForm({
  errors,
  isSuccess,
  isPending,
  onSubmit,
}: SellerSignUpFormProps) {
  const { mutateAsync: photoValidator } = useValidateSellerOrderPhotos();
  const {
    validateFiles,
    fileMustBeValidated,
    fileValidationFailed,
    validationStatus,
    getFileValidationErrorMessage,
  } = useImageValidationStatus({
    onValidationChange: () => {
      handleFilePickerChange(filePickerField.value as File[]);
    },
    errorResponseFieldName: 'images',
    photoValidator: photoValidator,
  });

  const dragAndDropText = () => {
    return (
      <Translate
        id={`customer-platform.shared.components.forms.listing-form.filepicker.drag-and-drop.text`}
        values={{
          span: (chunks: string[]) => (
            <Typography
              color={theme.palette.neutral[500]}
              component="span"
              variant="p1"
            >
              {head(chunks)}
              <RiArrowDownSFill color={theme.palette.neutral[500]} size={16} />
            </Typography>
          ),
        }}
      />
    );
  };

  const dragAndDropActiveText = () => {
    return (
      <Translate
        id={`customer-platform.shared.components.forms.listing-form.filepicker.drag-and-drop.active-text`}
      />
    );
  };

  const handleFilePickerChange = (
    files: ISellerSignUpData['materialPhotos'],
  ) => {
    const errorList = (formErrors?.materialPhotos || []) as FieldError[];
    const validFiles = files?.filter((file, index) => {
      return (
        Object.values(SUPPORTED_FORMATS).includes(file.type) &&
        file.size <= MAX_FILE_SIZE &&
        !fileValidationFailed(file) &&
        !(index in errorList)
      );
    });
    if (validFiles) {
      const filesToValidate = validFiles.filter(file =>
        fileMustBeValidated(file),
      );
      validateFiles(filesToValidate);
    }
    setInvalidFiles(
      files
        ?.map((file, index) => ({ file, originalIndex: index }))
        ?.filter(({ file }) => !validFiles?.includes(file))
        ?.map(({ file, originalIndex }) => {
          const errorMessage = [
            !Object.values(SUPPORTED_FORMATS).includes(file.type) && fileType,
            file.size > MAX_FILE_SIZE && fileSize,
            originalIndex in errorList && errorList[originalIndex]?.message,
            fileValidationFailed(file) && getFileValidationErrorMessage(file),
          ]
            .filter(Boolean)
            .join(' ');
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            errorMessage: errorMessage,
          } as IFilePickerFile;
        }) || [],
    );
    materialPhotosField.onChange(validFiles);
  };

  const [invalidFiles, setInvalidFiles] = useState<IFilePickerFile[]>([]);
  const intl = useIntl();
  const theme = useTheme();

  const formId = 'seller-signup-form.form';

  const required = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.required',
  });
  const validPhoneNumber = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.phone_number.invalid',
  });
  const validEmail = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.email.invalid',
  });
  const maxNumbersAfterDecimal = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.number.max_numbers_after_decimal',
    },
    { MAX_NUMBER_AFTER_DECIMAL: MAX_NUMBER_AFTER_DECIMAL.toString() },
  );
  const minQuantityRequired = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.quantify.min-quantity-required',
    },
    { MIN_QUANTITY_REQUIRED_MT: MIN_QUANTITY_REQUIRED_MT.toString() },
  );
  const minNumberOfFiles = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.filepicker.number_of_files',
    },
    { NUMBER_OF_FILES: MIN_REQUIRED_FILES.toString() },
  );
  const fileSize = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.filepicker.file_size',
    },
    { MAX_FILE_SIZE: humanFileSize(MAX_FILE_SIZE) },
  );
  const fileType = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.filepicker.file_type',
    },
    { SUPPORTED_FORMATS: SUPPORTED_FORMATS.join(', ') },
  );

  const sellerSignUpFormSchema = getSellerSignUpFormSchema({
    required,
    fileSize,
    fileType,
    minNumberOfFiles,
    maxNumbersAfterDecimal,
    minQuantityRequired,
    validPhoneNumber,
    validEmail,
  });
  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    clearErrors,
    formState: { errors: formErrors },
  } = useForm<FormType<ISellerSignUpData>>({
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      quantity: undefined,
      email: '',
      phoneNumber: '',
      metalType: '',
      productDescription: undefined,
      whatsappConsent: false,
    },
    resolver: yupResolver<FormType<ISellerSignUpData>>(sellerSignUpFormSchema),
  });

  const { field: metalTypeField } = useController({
    name: 'metalType',
    control,
  });

  const { field: filePickerField } = useController({
    name: 'materialPhotos',
    control,
  });

  const metalTypeData = buildSelectDataByOptionsAndNamespace(METAL_TYPE, intl);

  const handleStringChange =
    (field: ControllerRenderProps) => (value: string) => {
      field.onChange(value);
    };

  const handleSelectChange =
    (field: ControllerRenderProps) => (event: SelectChangeEvent<unknown>) => {
      field.onChange(event.target.value);
    };

  const { field: phoneNumberField } = useController({
    name: 'phoneNumber',
    control,
  });

  const { field: whatsappConsentField } = useController({
    name: 'whatsappConsent',
    control,
  });

  const { field: materialPhotosField } = useController({
    name: 'materialPhotos',
    control,
  });

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>) => {
    whatsappConsentField.onChange(event.target.checked);
  };

  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) {
      clearErrors();
      return;
    }
    Object.entries(errors).forEach(([field, messages]) =>
      setError(field as ISellerSignUpKeys, {
        type: 'manual',
        message: (messages?.[0] ?? '') as string,
      }),
    );
  }, [errors, setError, clearErrors]);

  useFormTracking({ formId, formErrors, isSuccess });

  useEffect(() => {
    if (isSuccess) {
      reset();
    }
  }, [reset, isSuccess]);

  return (
    <FormContainer>
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        id={formId}
        name={formId}
      >
        <FormBox>
          <FieldsWrapper>
            <Select
              id="metal-type"
              label={
                <Translate id="customer-platform.shared.components.forms.listing-form.select.metal.type" />
              }
              options={metalTypeData.options}
              value={metalTypeField.value}
              onChange={handleSelectChange(
                metalTypeField as ControllerRenderProps,
              )}
              error={!!formErrors?.metalType}
              helperText={formErrors?.metalType?.message}
              required
              formId={formId}
            />

            <TextField
              id="quantity"
              type="number"
              label={
                <Translate id="customer-platform.shared.components.forms.listing-form.textfield.quantity" />
              }
              {...register('quantity')}
              error={!!formErrors?.quantity}
              helperText={formErrors?.quantity?.message}
              required
              formId={formId}
            />

            <DualBox>
              <TextField
                id="first-name"
                label={
                  <Translate id="customer-platform.signup.forms.seller-signup.textfield.first_name" />
                }
                {...register('firstName')}
                error={!!formErrors?.firstName}
                helperText={formErrors?.firstName?.message}
                required
                formId={formId}
              />
              <TextField
                id="last-name"
                label={
                  <Translate id="customer-platform.signup.forms.seller-signup.textfield.last_name" />
                }
                {...register('lastName')}
                error={!!formErrors?.lastName}
                helperText={formErrors?.lastName?.message}
                required
                formId={formId}
              />
            </DualBox>

            <TextField
              id="company-name"
              label={
                <Translate id="customer-platform.signup.forms.seller-signup.textfield.company_name" />
              }
              {...register('companyName')}
              error={!!formErrors?.companyName}
              helperText={formErrors?.companyName?.message}
              required
              formId={formId}
            />
            <TextField
              id="email"
              type="email"
              label={
                <Translate id="customer-platform.signup.forms.seller-signup.textfield.email" />
              }
              {...register('email')}
              error={!!formErrors?.email}
              helperText={formErrors?.email?.message}
              required
              formId={formId}
            />
            <PhoneField
              id="phone-number"
              label={
                <Translate id="customer-platform.signup.forms.seller-signup.textfield.phone_number" />
              }
              defaultCountry="DE"
              value={phoneNumberField.value}
              onChange={handleStringChange(
                phoneNumberField as ControllerRenderProps,
              )}
              error={!!formErrors.phoneNumber}
              helperText={formErrors?.phoneNumber?.message}
              formId={formId}
              required
            />
            <TextArea
              id="product-description"
              data-testid="product-description"
              placeholder={intl.formatMessage({
                id: 'customer-platform.signup.forms.seller-signup.textfield.product_description',
              })}
              {...register('productDescription')}
              error={!!formErrors?.productDescription}
              helperText={formErrors?.productDescription?.message}
              formId={formId}
              minRows={2.25}
            />
            <FilePicker
              id="material-photos-field"
              formId={formId}
              label={
                <Translate id="customer-platform.shared.components.forms.listing-form.filepicker.label" />
              }
              dragAndDropText={dragAndDropText()}
              dragAndDropActiveText={dragAndDropActiveText()}
              invalidValues={invalidFiles}
              value={
                materialPhotosField.value as ISellerSignUpData['materialPhotos']
              }
              onChange={handleFilePickerChange}
              errorMessageList={
                formErrors?.materialPhotos?.constructor === Array
                  ? formErrors?.materialPhotos
                  : []
              }
              helperText={formErrors?.materialPhotos?.message}
              validationStatusList={validationStatus}
              required
            />
            <CheckBox
              id="whatsapp-consent"
              label={
                <Translate id="customer-platform.signup.forms.seller-signup.checkbox.whatsapp_consent" />
              }
              value={whatsappConsentField.value as boolean}
              onChange={handleCheckBoxChange}
              error={!!formErrors?.whatsappConsent}
              helperText={formErrors?.whatsappConsent?.message}
              formId={formId}
              size="small"
            />
          </FieldsWrapper>

          <ButtonsBox>
            <Button
              buttonType="primary"
              id="seller-form.button.submit"
              data-testid="seller-form.button.submit"
              size="large"
              type="submit"
              isSubmitting={isPending}
            >
              <Translate id="customer-platform.signup.cta" />
            </Button>
          </ButtonsBox>
        </FormBox>
      </form>
    </FormContainer>
  );
}
